import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Bell, X, ChevronUp } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const LABELS = {
  requests: "demandes",
  signups: "inscriptions partenaire",
  partner_fiches: "fiches à vérifier",
  pros: "nouveaux partenaires",
  offers: "offres à modérer",
  reviews: "avis à modérer",
  reports: "signalements",
  support: "tickets support",
  orgs: "organismes à valider",
  recos: "recommandations",
  fbposts: "publications Facebook",
  associations: "associations à valider",
  clubs: "clubs à valider",
};
const ADMIN_ROLES = ["admin", "super_admin", "manager"];
const POLL_MS = 60000;
const BASE_FAVICON = "/pwa-icon.svg";

// Favicon de base P28 + pastille rouge avec le nombre d'actions en haut à droite.
function badgedFaviconUrl(n) {
  const count = n > 9 ? "9+" : String(n);
  const fontSize = count.length > 1 ? 82 : 104;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
  <defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
    <stop offset="0" stop-color="#F7941E"/><stop offset="1" stop-color="#E8521B"/>
  </linearGradient></defs>
  <rect width="512" height="512" rx="112" fill="url(#g)"/>
  <path d="M176,196 C176,148 336,148 336,196" fill="none" stroke="#1B2F56" stroke-width="26" stroke-linecap="round"/>
  <rect x="130" y="194" width="252" height="236" rx="40" fill="#fff"/>
  <g stroke="#1B2F56" stroke-width="24" fill="none" stroke-linecap="round"><line x1="318" y1="262" x2="208" y2="392"/><circle cx="224" cy="284" r="21"/><circle cx="302" cy="372" r="21"/></g>
  <circle cx="404" cy="108" r="124" fill="#fff"/>
  <circle cx="404" cy="108" r="103" fill="#EF4444"/>
  <text x="404" y="108" text-anchor="middle" dominant-baseline="central" font-family="Inter, Helvetica, Arial, sans-serif" font-size="${fontSize + 10}" font-weight="900" fill="#fff">${count}</text>
</svg>`;
  return "data:image/svg+xml," + encodeURIComponent(svg);
}

function setFavicon(href) {
  let link = document.querySelector('link[rel="icon"]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.type = "image/svg+xml";
  if (link.getAttribute("href") !== href) link.setAttribute("href", href);
}

export default function AdminSiteAlert() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [data, setData] = useState({ total: 0, sections: {} });
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [dismissedAt, setDismissedAt] = useState(-1);

  const isAdminUser = user && user !== false && ADMIN_ROLES.includes(user.role);
  const active = isAdminUser && !pathname.startsWith("/admin");

  useEffect(() => {
    if (!isAdminUser) return;
    let stop = false;
    const load = async () => {
      try {
        const { data } = await api.get("/admin/todos");
        if (!stop) setData(data);
      } catch {}
    };
    load();
    const iv = setInterval(load, POLL_MS);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => {
      stop = true;
      clearInterval(iv);
      window.removeEventListener("focus", onFocus);
    };
    // eslint-disable-next-line
  }, [isAdminUser]);

  // Pastille dans l'onglet du navigateur : préfixe (N) sur le titre tant qu'il y a des actions.
  // Un MutationObserver réapplique le préfixe si la page change son <title> (navigation SPA).
  useEffect(() => {
    const n = isAdminUser ? (data.total || 0) : 0;
    const titleEl = document.querySelector("title");
    let guard = false;
    const apply = () => {
      if (guard) return;
      const base = document.title.replace(/^\(\d+\)\s*/, "");
      const next = n > 0 ? `(${n}) ${base}` : base;
      if (next !== document.title) { guard = true; document.title = next; guard = false; }
    };
    apply();
    const obs = titleEl ? new MutationObserver(apply) : null;
    if (obs && titleEl) obs.observe(titleEl, { childList: true });
    // Favicon dynamique : pastille rouge dans l'onglet tant qu'il y a des actions.
    setFavicon(n > 0 ? badgedFaviconUrl(n) : BASE_FAVICON);
    return () => {
      if (obs) obs.disconnect();
      document.title = document.title.replace(/^\(\d+\)\s*/, "");
      setFavicon(BASE_FAVICON);
    };
  }, [data.total, isAdminUser, pathname]);

  if (!active) return null;
  const total = data.total || 0;
  if (total === 0 || total === dismissedAt) return null;

  const items = Object.entries(data.sections || {}).filter(([, v]) => v > 0);

  return (
    <div
      className="fixed left-4 bottom-20 lg:bottom-4 z-40 w-[260px] max-w-[calc(100vw-2rem)]"
      data-testid="admin-site-alert"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {(open || hovered) && (
        <div className="mb-2 rounded-xl border border-slate-200 bg-white shadow-xl overflow-hidden animate-in fade-in slide-in-from-bottom-2">
          <div className="px-3 py-2 border-b border-slate-100 text-[11px] font-bold uppercase tracking-wide text-slate-500">
            À traiter au back-office
          </div>
          <div className="max-h-56 overflow-y-auto">
            {items.map(([k, v]) => (
              <Link
                key={k}
                to="/admin"
                onClick={() => setOpen(false)}
                className="flex items-center justify-between px-3 py-2 text-sm hover:bg-slate-50 border-b border-slate-50 last:border-b-0"
                data-testid={`admin-site-alert-item-${k}`}
              >
                <span className="text-slate-700 capitalize">{LABELS[k] || k}</span>
                <span className="min-w-[20px] h-5 px-1.5 grid place-items-center rounded-full bg-rose-500 text-white text-[11px] font-extrabold">
                  {v}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-stretch rounded-full bg-[#1F2937] text-white shadow-lg ring-1 ring-black/5 overflow-hidden">
        <Link
          to="/admin"
          className="flex items-center gap-2 pl-3 pr-2 py-2 hover:bg-[#2b3a4d] transition-colors"
          data-testid="admin-site-alert-link"
        >
          <span className="relative grid place-items-center">
            <Bell size={16} className="text-[#F6B24A]" />
            <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-rose-500 ring-2 ring-[#1F2937]" />
          </span>
          <span className="text-[13px] font-semibold leading-tight">
            <span className="text-[#F6B24A] font-extrabold">{total}</span> action{total > 1 ? "s" : ""} à traiter
          </span>
        </Link>
        <button
          onClick={() => setOpen((o) => !o)}
          aria-label="Détail des actions"
          data-testid="admin-site-alert-toggle"
          className="px-2 border-l border-white/10 hover:bg-[#2b3a4d] transition-colors"
        >
          <ChevronUp size={15} className={`transition-transform ${open ? "" : "rotate-180"}`} />
        </button>
        <button
          onClick={() => { setDismissedAt(total); setOpen(false); }}
          aria-label="Masquer"
          data-testid="admin-site-alert-dismiss"
          className="px-2 border-l border-white/10 hover:bg-[#2b3a4d] transition-colors"
        >
          <X size={15} />
        </button>
      </div>
    </div>
  );
}
